import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountService } from 'src/app/core/services/account.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ImageCroppedEvent, base64ToFile } from 'ngx-image-cropper';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthfakeauthenticationService } from 'src/app/core/services/authfake.service';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';


@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrl: './account.component.scss',
    standalone: false
})
export class AccountComponent {

  profileForm!: FormGroup;
  securityForm!: FormGroup;
  notificationForm!: FormGroup;
  brandingForm!: FormGroup;
  notificationSubmitted = false;
  fileError: string | null = null;
  companyLogo: File | null = null;
  profileSubmitted = false;
  securitySubmitted = false;
  customerProfile: any = {};
  customerEstampProfile: any = {};
  loading: boolean = false;
  pageLoading: boolean = false;
  logo: string | null = null;
  imageChangedEvent: any = null;
  croppedImage: any;
  showCropper: boolean = false;
  previewImage: string = '';
  @ViewChild('fileInput') fileInput!: ElementRef;
  activeTab: 'esign' | 'estamp' = 'esign';



  constructor(private fb: FormBuilder, private accountService: AccountService, private toastService: ToastService,
    private router: Router, private authService: AuthfakeauthenticationService, private activatedRoute: ActivatedRoute, private confirmModalService: ConfirmModalService
  ) { }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((query: any) => {
      if (query.profile === 'esign' || query.profile === 'estamp') {
        this.activeTab = query.profile;
      }
    })
    this.getCustomerProfile();
    this.getEstampCustomerProfile();

    this.profileForm = this.fb.group({
      company: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/), Validators.minLength(10), Validators.maxLength(10)]],
    });

    this.securityForm = this.fb.group(
      {
        oldPassword: ['', Validators.required],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).+$/)
          ]
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: this.passwordMatchValidator
      }
    );
    this.securityForm.get('newPassword')?.valueChanges.subscribe((value) => {

      this.passwordErrors = [];

      if (!value) {
        return;
      }

      if (value.length < 8) {
        this.passwordErrors.push('Minimum 8 characters required');
      }

      if (!/[A-Z]/.test(value)) {
        this.passwordErrors.push('At least 1 uppercase letter required');
      }

      if (!/[a-z]/.test(value)) {
        this.passwordErrors.push('At least 1 lowercase letter required');
      }

      if (!/\d/.test(value)) {
        this.passwordErrors.push('At least 1 number required');
      }

      if (!/[@$!%*?&]/.test(value)) {
        this.passwordErrors.push('At least 1 special character required');
      }

    });

    this.notificationForm = this.fb.group({
      email: [false],
      sms: [false],
      whatsApp: [false],
      weeklyUsageReports: [false]
    }, {
      validators: this.atLeastOneSelectedValidator
    });

    this.brandingForm = this.fb.group({
      logo: [null, Validators.required],
      primaryColor: ['var(--primary-color)', Validators.required],
      secondaryColor: ['#54BE96', Validators.required]
    });
  }

  getCustomerProfile() {
    this.loading = true;

    this.accountService.getCustomerProfile().subscribe({
      next: (res: any) => {
        this.customerProfile = res;

        const branding = res?.brandingSettings;
        const notification = res?.notificationSettings;

        if (res?.email || res?.company || res?.phone) {
          this.profileForm.patchValue({
            email: res?.email ?? '',
            company: res?.company ?? '',
            phone: res?.phone ?? ''
          });
        }

        if (notification) {
          this.notificationForm.patchValue({
            email: notification?.email,
            sms: notification?.sms,
            whatsApp: notification?.whatsApp,
            weeklyUsageReports: notification?.weeklyUsageReports
          });
        }

        if (branding) {
          this.brandingForm.patchValue({
            logo: branding?.logo ?? null,
            primaryColor: branding?.primaryColor
              ? `#${branding.primaryColor}`
              : '#000000',
            secondaryColor: branding?.secondaryColor
              ? `#${branding.secondaryColor}`
              : '#000000'
          });

          if (branding?.logo) {
            this.logo = branding.logo;
          }
        }

        this.loading = false; // IMPORTANT
      },

      error: (err) => {
        this.loading = false; // IMPORTANT
      }
    });
  }

  getEstampCustomerProfile() {
    this.loading = true;
    this.accountService.getEstampCustomerProfile()
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          this.customerEstampProfile = res?.data;
        },
        error: (err) => {
          this.loading = false;
        }
      });
  }

  get profileControls() { return this.profileForm.controls; }
  get securityControls() { return this.securityForm.controls; }
  passwordErrors: string[] = [];

  updateProfile() {
    this.profileSubmitted = true;
    if (this.profileForm.invalid) return;

    const profileData = this.profileForm.value;
    this.accountService.updateProfile(profileData, this.customerProfile?._id).subscribe(() => {
      this.getCustomerProfile();
      this.toastService.showToaster('Profile Updated', 'Profile updated successfully.', 'success');
    });
  }

  confirmToggleEstamp() {

    if (!this.customerProfile?._id) {
      this.toastService.showToaster(
        'Error',
        'Customer not found',
        'error'
      );
      return;
    }

    const message = this.customerEstampProfile?.estampService === 'ACTIVE'
      ? 'Are you sure you want to deactivate E-Stamp Service?'
      : 'Are you sure you want to activate E-Stamp Service?'

    this.confirmModalService.open(
      'E-Stamp Service',
      message,
      () => {
        this.toggleEstampService();
      },
      this.customerEstampProfile?.estampService === 'ACTIVE'
        ? 'Deactivate'
        : 'Activate',
      'Cancel'
    );

  }

  toggleEstampService() {
    this.loading = true;

    this.accountService.estampRequest().subscribe({
      next: (res: any) => {
        this.loading = false;
        this.getEstampCustomerProfile();
        this.toastService.showToaster(
          'E-stamp Service',
          res?.message,
          'success'
        );
      },
      error: (err: any) => {
        this.loading = false;
      }
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (
      confirmPassword.value &&
      newPassword.value !== confirmPassword.value
    ) {
      confirmPassword.setErrors({ mismatch: true });
    } else {
      if (confirmPassword.hasError('mismatch')) {
        confirmPassword.setErrors(null);
      }
    }

    return null;
  }

  updateSecurity() {
    this.securitySubmitted = true;
    if (this.securityForm.invalid) return;
    if (this.securityControls.newPassword.value !== this.securityControls.confirmPassword.value) {
      this.toastService.showToaster('Password Mismatch', 'New Password and Confirm New Password do not match.', 'info');
      return;
    }

    const securityData = this.securityForm.value;
    this.accountService.updatePassword(securityData).subscribe(() => {
      this.getCustomerProfile();
      this.toastService.showToaster('Profile Updated', 'Password updated successfully.', 'success');
      this.securityForm.reset();
      this.authService.logout();
      this.router.navigate(['/account/login']);
    });
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 500 * 1024) {
      this.fileError = 'File size must be less than 500KB';
      return;
    }

    this.fileError = null;
    this.imageChangedEvent = event;
    this.companyLogo = file;
    this.showCropper = true;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.previewImage = event.objectUrl || '';

    if (event.blob) {
      this.companyLogo = new File(
        [event.blob],
        'cropped-image.png',
        { type: event.blob.type }
      );
      this.brandingForm.patchValue({
        logo: this.companyLogo
      });
    }
  }

  confirmCrop() {
    if (!this.companyLogo) {
      this.toastService.showToaster('Error', 'Please crop the image first.', 'error');
      return;
    }
    this.showCropper = false;
  }

  closeCropper() {
    this.showCropper = false;
    this.imageChangedEvent = null;
    this.previewImage = '';
    this.companyLogo = null;
    this.fileError = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  removeLogo() {
    this.logo = null;
    this.previewImage = '';
    this.companyLogo = null;
    this.brandingForm.patchValue({ logo: null });
    this.fileError = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.getCustomerProfile();
  }

  atLeastOneSelectedValidator(form: FormGroup) {
    const controls = form.controls;
    const hasOneSelected = Object.keys(controls).some(key => controls[key].value === true);
    return hasOneSelected ? null : { atLeastOneRequired: true };
  }

  saveBranding() {
    if (!this.companyLogo || this.brandingForm.invalid) {
      this.toastService.showToaster('Validation Error', 'Please add logo of your organisation.', 'info');
      return;
    }

    const formData = new FormData();
    formData.append('logo', this.companyLogo);
    formData.append('primaryColor', this.brandingForm.value.primaryColor.replace('#', ''));
    formData.append('secondaryColor', this.brandingForm.value.secondaryColor.replace('#', ''));
    this.accountService.saveBranding(this.customerProfile._id, formData).subscribe({
      next: () => {
        this.toastService.showToaster('Success', 'Branding saved successfully.', 'success')
        this.getCustomerProfile();
        this.previewImage = '';
      },
      error: () => {}
    });
  }

  updateNotificationPreference() {
    this.notificationSubmitted = true;
    const data = this.notificationForm.value;

    if (this.notificationForm.invalid) {
      this.toastService.showToaster('Validation Error', 'Please select at least one notification option.', 'info');
      return;
    }

    if (!this.notificationForm.dirty) {
      return;
    }

    if (!this.customerProfile?._id) {
      this.toastService.showToaster('Error', 'Customer not found', 'error');
      return;
    }

    this.accountService.updateNotification(data, this.customerProfile._id).subscribe({
      next: () => {
        this.getCustomerProfile();
        this.notificationForm.markAsPristine();
        this.toastService.showToaster(
          'Notification',
          'Notification preferences saved successfully.',
          'success'
        );
      },
      error: () => {}
    });
  }

  confirmPassword: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  togglePassword(field: string) {
    switch (field) {
      case 'confirmPassword':
        this.confirmPassword = !this.confirmPassword;
        break;
      case 'showPassword':
        this.showPassword = !this.showPassword;
        break;
      case 'showConfirmPassword':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  switchTab(tab: 'esign' | 'estamp') {
    this.activeTab = tab;
  }


}
